import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subject, merge, EMPTY, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, filter, tap, catchError, switchMap, finalize } from 'rxjs/operators';

import { each, cloneDeep, findIndex, has } from 'lodash';
import { NgbModal, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';

import { MigrateEmployer, Responsible, fieldsSectorDescription } from './sectors-create-edit-widget';
import { SectorsService } from '../../sectors.service';
import { ConfirmModalComponent } from '../../../../shared/modals/confirm-modal/confirm-modal.component';
import { BranchNames, DistrictNames, Sector, SectorNames, SectorRole, SECTORS_PATHS } from '../../sectors';
import { Enums } from '../../../../configs/Enums';
import { checkSectorValidator, minLengthArray } from './sectors-create-edit-widget-validators';
import { CompanyNames, StreetType } from '../../../companies/companies';
import { DistrictCoefficientsComponent } from '../district-coefficients/district-coefficients.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-sectors-create-edit-widget',
  templateUrl: './sectors-create-edit-widget.component.html',
  styleUrls: ['./sectors-create-edit-widget.component.scss'],
  standalone: false,
})
export class SectorsCreateEditWidgetComponent implements OnInit {
  public dataLoading = false;
  public plotId: string | number;
  public isEditMode: boolean;
  public plotCreateEditForm: UntypedFormGroup;
  public companiesList: CompanyNames[];
  public companySelected: CompanyNames;
  public regionsList: string[];
  public regionSelected: string;
  public branchesList: BranchNames[];
  public branchSelected: BranchNames;
  public citiesList: string[];
  public citySelected: string;
  public streetTypesList: StreetType[];
  public streetTypeSelected: StreetType;
  public responsibleList: Responsible[];
  public responsibleSelected: Responsible;
  public districtList: DistrictNames[];
  public districtSelected: DistrictNames;
  public roleSelected: ISelectOption<SectorRole>;
  public districtGroupList = {};
  public employersSectorsList: MigrateEmployer[];
  public sectorSelectedForMigrateUsers = null;
  public migrateSectorsList: SectorNames[];
  public isSomeMigrateEmployeeChecked = false;
  public isAllEmployeeChecked: boolean;
  public isVisibleMigrateEmployersBlock: boolean;
  public isNotFirstInitResponsible: boolean;
  public fieldsInvalid;
  public backendError: string;
  public fieldsDescription = fieldsSectorDescription;
  public currentSelectedPlot: Sector;
  public plotVersions: Sector[];
  public versions: string[];
  public sectorNumberData: BranchNames;
  public saveOrCreateLoading: boolean;
  public migrateLoading: boolean;
  public roleList: ISelectOption<SectorRole>[] = [
    { name: 'Подключение и ремонт клиентов', value: SectorRole.MT },
    { name: 'Ремонт районных сетей', value: SectorRole.GRRS },
  ];

  @ViewChild('instance') instance: NgbTypeahead;
  @ViewChild(DistrictCoefficientsComponent) districtCoefficients: DistrictCoefficientsComponent;
  focusCity$ = new Subject<string>();
  clickCity$ = new Subject<string>();

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public sectorsService: SectorsService,
    public modalService: NgbModal,
    private fb: UntypedFormBuilder
  ) {}

  get sectorRoleDisabled() {
    return !this.plotCreateEditForm.get('sector_role').value;
  }

  get showDistrictCoefficients(): boolean {
    return this.plotCreateEditForm?.get('sector_role')?.value === SectorRole.MT;
  }

  /** Автоматическое распределение доступно для всех участков, кроме ГРРС */
  get autoDistributionAvailable(): boolean {
    return this.plotCreateEditForm?.get('sector_role')?.value !== SectorRole.GRRS;
  }

  get isVisibleSector(): boolean {
    if (!this.plotCreateEditForm) {
      return true;
    }
    const control = this.plotCreateEditForm.get('is_visible');
    return control ? control.value : true;
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.plotId = params.get('sector_id');
      this.isEditMode = !!this.plotId;

      if (this.plotId) {
        this.getEditData();
      } else {
        this.getCreateData();
      }
    });
  }

  public getMigrateUsers(): UntypedFormArray {
    return this.plotCreateEditForm.get('migrate_users') as UntypedFormArray;
  }

  searchCity = (text$: Observable<any>): any => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.clickCity$.pipe(filter(() => !this.instance.isPopupOpen()));
    const inputFocus$ = this.focusCity$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map((term) => (term === '' ? this.citiesList : this.citiesList.filter((v) => v.toLowerCase().indexOf(term.toLowerCase()) > -1)))
    );
  };

  public onChangeRole(selectedRole): void {
    this.roleSelected = selectedRole;
    this.plotCreateEditForm.get('sector_role').setValue(selectedRole.value);
    this.getCreateEditExpandedData().subscribe();
  }

  private getCreateData(): void {
    this.dataLoading = true;

    this.sectorsService.getCreatePlotUnion().subscribe(
      (response) => {
        this.regionsList = response[0]['data'];
        this.streetTypesList = response[1]['data'];
        this.citiesList = response[2]['data'];
        this.companiesList = response[3]['data']
          .filter((c) => c.is_visible)
          .map((company) => {
            company.new_name = company.ownership_type.ot_short + ' "' + company.ps_name + '"';
            return company;
          });
        this.makeCreatePlotForm();
        this.plotCreateEditForm.updateValueAndValidity();

        this.dataLoading = false;
      },
      (error) => {
        console.log(error);
        this.dataLoading = false;
      }
    );
  }

  private getEditData(): void {
    this.dataLoading = true;

    this.sectorsService.getEditPlotUnion(this.plotId).subscribe(
      (response) => {
        this.regionsList = response[0]['data'];
        this.streetTypesList = response[1]['data'];
        this.citiesList = response[2]['data'];
        this.plotVersions = response[3]['data'];
        this.companiesList = response[4]['data'].map((company) => {
          company.new_name = company.ownership_type.ot_short + ' "' + company.ps_name + '"';
          return company;
        });
        this.makePlotVersions();
        this.makeEditPlotForm();
        this.plotCreateEditForm.updateValueAndValidity();

        this.dataLoading = false;
      },
      (error) => {
        console.log(error);
        this.dataLoading = false;
      }
    );
  }

  private getCreateEditExpandedData(): Observable<void> {
    const sectorId = (this.currentSelectedPlot && this.currentSelectedPlot.sector_id) || '';

    if (!this.branchSelected || !this.roleSelected) {
      return EMPTY;
    }

    const isSelected = (d) => {
      if (!this.currentSelectedPlot || !Array.isArray(this.currentSelectedPlot.districts)) {
        return;
      }

      const foundDistrict = this.currentSelectedPlot.districts.find((pDistrict) => {
        return pDistrict.districts.find((pd) => pd.d_id === d.d_id);
      });

      return Boolean(foundDistrict);
    };

    this.responsibleSelected = null;
    return this.sectorsService.getCreateEditExpandedData(this.branchSelected.mb_id, sectorId, this.roleSelected.value).pipe(
      tap((response) => {
        this.districtList = response[0]['data'].map((district) => {
          district.new_district_name = district.district_name + ' <span class="text-italic color-gray-dark ml-8">' + district.city.ct_name + '</span>';
          district.is_checked = (this.isEditMode && district.sector_id && district.sector_id === this.currentSelectedPlot.sector_id) || isSelected(district);
          return district;
        });

        this.sectorNumberData = response[1]['data'];

        this.plotCreateEditForm.controls['sector_id'].setValue((this.sectorNumberData && this.sectorNumberData.next_sector_id) || null);

        this.onCloseSearchPopover();
      }),
      catchError((error) => {
        console.log(error);
        return of(error);
      })
    );
  }

  private getResponsiblies(): void {
    const params = {
      mb_id: (this.branchSelected && this.branchSelected.mb_id) || '',
      partnership_id: (this.companySelected && this.companySelected.campaign_id) || '',
      sector_id: (this.currentSelectedPlot && this.currentSelectedPlot.sector_id) || '',
    };

    if (!this.branchSelected && !this.companySelected) {
      return;
    }

    this.sectorsService.getResponsible(params).subscribe(
      (response) => {
        this.responsibleList = response['data'];

        if (this.isEditMode && !this.isNotFirstInitResponsible) {
          this.isNotFirstInitResponsible = true;
          const employeeId = this.currentSelectedPlot.sector_head && this.currentSelectedPlot.sector_head.u_id;
          const employeeIndex = findIndex(this.responsibleList, {
            user_id: employeeId,
          });

          if (employeeIndex > -1) {
            this.onChangeResponsible(this.responsibleList[employeeIndex]);
            this.responsibleSelected = this.responsibleList[employeeIndex];
          } else {
            this.onChangeResponsible(null);
          }
        }
      },
      (error) => {
        this.responsibleList = null;
      }
    );
  }

  private getMigrateSectors(): void {
    if (this.migrateSectorsList && this.isVisibleMigrateEmployersBlock) {
      return;
    }

    this.migrateLoading = true;
    this.sectorsService
      .getFilterPlots({
        mb_id: this.currentSelectedPlot.market_branch.mb_id,
        partnership_id: this.plotCreateEditForm.get('partnership_id').value,
      })
      .subscribe(
        (response) => {
          // Исключаем видимые, а также сектор, что в данный момент редактируем.
          this.migrateSectorsList = response['data'].filter((s) => {
            return s.is_visible && s.sector_id !== this.currentSelectedPlot.sector_id;
          });
          this.getEmployersUnbind();
        },
        (error) => {
          console.log(error);
          this.migrateSectorsList = null;
          this.migrateLoading = false;
        }
      );
  }

  private getEmployersUnbind(): void {
    this.sectorsService.getEmployersUnbind(this.plotId).subscribe(
      (response) => {
        const migrateUsers = this.getMigrateUsers();
        this.employersSectorsList = response['data'];
        each(this.employersSectorsList, (employee) => {
          employee.sector_id = null;
          employee.sector = null;
          employee.is_checked = false;
          // Не добавляем валидатор если участок отключается (is_visible = false)
          // В этом случае миграция сотрудников необязательна
          const validators = this.isVisibleSector ? [checkSectorValidator('sector_id')] : [];
          migrateUsers.push(new UntypedFormControl(employee, validators));
        });
        this.migrateLoading = false;
      },
      (error) => {
        console.log(error);
        this.employersSectorsList = null;
        this.migrateLoading = false;
      }
    );
  }

  private makePlotVersions(): void {
    this.currentSelectedPlot = this.plotVersions[this.plotVersions.length - 1];
    this.versions = this.plotVersions.map((ver) => {
      return ver.version_number;
    });
  }

  private makeCreatePlotForm(): void {
    this.plotCreateEditForm = new UntypedFormGroup({
      s_id: new UntypedFormControl(null),
      // не используются в форме, но нужны для бекенда
      country: new UntypedFormControl(null),
      city_type: new UntypedFormControl(null),
      //
      partnership_id: new UntypedFormControl('', Validators.required),
      region_name: new UntypedFormControl('', Validators.required),
      market_branch: new UntypedFormControl('', Validators.required),
      city_name: new UntypedFormControl('', Validators.required),
      street_type: new UntypedFormControl('', Validators.required),
      street_name: new UntypedFormControl('', Validators.required),
      house_number: new UntypedFormControl('', Validators.required),
      sector_head: new UntypedFormControl(''),
      latitude: new UntypedFormControl('', Validators.required),
      longitude: new UntypedFormControl('', Validators.required),
      house_building: new UntypedFormControl(''),
      office_number: new UntypedFormControl(''),
      districts: this.fb.array([], minLengthArray(1)),
      is_visible: new UntypedFormControl(1),
      sector_id: new UntypedFormControl('', Validators.required),
      migrate_users: new UntypedFormControl(null),
      sector_role: new UntypedFormControl(null, Validators.required),
    });

    const streetTypeIndex = findIndex(this.streetTypesList, { st_id: 1 });

    if (streetTypeIndex > -1) {
      this.onChangeStreetType(this.streetTypesList[streetTypeIndex]);
      this.streetTypeSelected = this.streetTypesList[streetTypeIndex];
    }

    this.onChangesFormSubscribe();
  }

  private makeEditPlotForm(): void {
    const plotData = this.currentSelectedPlot;

    this.plotCreateEditForm = new UntypedFormGroup({
      s_id: new UntypedFormControl(plotData.s_id),
      // не используются в форме, но нужны для бекенда
      country: new UntypedFormControl(null),
      city_type: new UntypedFormControl(null),
      //
      partnership_id: new UntypedFormControl(plotData.partnership_id, Validators.required),
      region_name: new UntypedFormControl(plotData.region_name, Validators.required),
      market_branch: new UntypedFormControl(plotData.market_branch.mb_id, Validators.required),
      city_name: new UntypedFormControl(plotData.city_name, Validators.required),
      street_type: new UntypedFormControl(plotData.street_type, Validators.required),
      street_name: new UntypedFormControl(plotData.street_name, Validators.required),
      house_number: new UntypedFormControl(plotData.house_number, Validators.required),
      sector_head: new UntypedFormControl(plotData.sector_head ? plotData.sector_head.u_id : ''),
      latitude: new UntypedFormControl('', Validators.required),
      sector_role: new UntypedFormControl(null, Validators.required),
      longitude: new UntypedFormControl('', Validators.required),
      house_building: new UntypedFormControl(plotData.house_building),
      office_number: new UntypedFormControl(plotData.office_number),
      districts: this.fb.array([], plotData.is_visible ? minLengthArray(1) : null),
      is_visible: new UntypedFormControl(plotData.is_visible),
      sector_id: new UntypedFormControl(plotData.sector_id, Validators.required),
      migrate_users: new UntypedFormControl(null),
    });

    this.initUiData();
    this.onChangesFormSubscribe();
  }

  private initUiData(): void {
    const plotData = this.currentSelectedPlot;
    const streetTypeId = plotData.street_type;
    const streetTypeIndex = findIndex(this.streetTypesList, {
      st_id: +streetTypeId,
    });
    const partnershipId = plotData.partnership_id;
    const companyIndex = findIndex(this.companiesList, {
      campaign_id: partnershipId,
    });

    if (streetTypeIndex > -1) {
      this.onChangeStreetType(this.streetTypesList[streetTypeIndex]);
      this.streetTypeSelected = this.streetTypesList[streetTypeIndex];
    }

    if (companyIndex > -1) {
      this.onChangeCompany(this.companiesList[companyIndex]);
    }
    this.plotCreateEditForm.get('sector_role').setValue(plotData.sector_role);
    this.plotCreateEditForm.controls['latitude'].setValue(plotData.latitude);
    this.plotCreateEditForm.controls['longitude'].setValue(plotData.longitude);

    this.branchSelected = plotData.market_branch;
    this.regionSelected = plotData.region_name;
    this.roleSelected = this.roleList.find((role) => role.value === plotData.sector_role);
    this.plotCreateEditForm.controls['sector_role'].setValue(this.roleSelected.value);
    this.getCreateEditExpandedData().subscribe(() => {
      if (Array.isArray(plotData.districts)) {
        plotData.districts.forEach((pd) => {
          pd.districts.forEach((d) => {
            (this.plotCreateEditForm.controls['districts'] as UntypedFormArray).push(new UntypedFormControl(d));
            this.districtAddGroup(d);
          });
        });
      }
    });
  }

  public onChangesFormSubscribe(): void {
    if (!this.isEditMode) {
      this.plotCreateEditForm.controls['is_visible'].disable();
      this.plotCreateEditForm.controls['is_visible'].updateValueAndValidity();
    }

    this.plotCreateEditForm.get('is_visible').valueChanges.subscribe((isChecked) => {
      this.onChangeSectorVisible(isChecked);
    });
  }

  public onChangeCompany(companySelected: CompanyNames): void {
    const currentSector = this.currentSelectedPlot;

    this.companySelected = companySelected;
    this.onChangeResponsible(null);
    this.plotCreateEditForm.controls['partnership_id'].setValue(companySelected.campaign_id || null);
    this.getResponsiblies();

    if (this.isEditMode && currentSector.is_visible && this.companySelected && this.companySelected.campaign_id !== currentSector.partnership_id) {
      this.plotCreateEditForm.setControl('migrate_users', new UntypedFormArray([]));
      this.getMigrateSectors();
    } else {
      this.plotCreateEditForm.setControl('migrate_users', new UntypedFormControl(null));
    }

    this.checkVisibleMigrateBlock();
  }

  public onChangeRegion(regionSelected: string): void {
    this.regionSelected = regionSelected ? regionSelected : null;
    this.onChangeBranch(null);
    this.districtList = null;
    this.citiesList = null;
    this.getCreateEditExpandedData().subscribe();
    this.onCloseSearchPopover();
    this.onChangeResponsible(null);

    if (this.regionSelected) {
      this.sectorsService.getBranches({ mb_regions: [this.regionSelected] }).subscribe((response) => {
        this.branchesList = response['data'];
      });
    } else {
      this.branchesList = null;
    }

    this.plotCreateEditForm.controls['region_name'].setValue(regionSelected || null);
  }

  public onChangeBranch(branchSelected: BranchNames): void {
    this.onChangeResponsible(null);
    this.districtSelected = null;
    this.districtList = null;
    this.citiesList = null;
    this.branchSelected = branchSelected;

    if (this.branchSelected) {
      this.sectorsService
        .getCitiesNames({
          mb_regions: [this.regionSelected],
          mb_ids: [this.branchSelected.mb_id],
        })
        .subscribe((response) => {
          this.citiesList = response['data'];
        });
    }

    this.plotCreateEditForm.controls['market_branch'].setValue((branchSelected && branchSelected.mb_id) || null);
    this.getCreateEditExpandedData().subscribe();
    this.getResponsiblies();
  }

  public onChangeStreetType(streetType: StreetType): void {
    this.streetTypeSelected = streetType;
    this.plotCreateEditForm.controls['street_type'].setValue((streetType && streetType.st_id) || null);
  }

  public onChangeResponsible(responsible: Responsible): void {
    this.responsibleSelected = responsible;
    this.plotCreateEditForm.controls['sector_head'].setValue((responsible && responsible.u_id) || (responsible && responsible.user_id) || null);
  }

  public onChangeCoords(coords: string, event): void {
    let val = coords || event.target.value;
    let gpsRegexp = /^(-?\d{1,2}\.\d{6}), (-?\d{1,2}\.\d{6})$/g;

    if (gpsRegexp.test(val)) {
      let gpsArr = val.split(', ');

      this.plotCreateEditForm.controls['latitude_longitude'].setErrors(null);
      this.plotCreateEditForm.controls['latitude'].setValue(gpsArr[0]);
      this.plotCreateEditForm.controls['longitude'].setValue(gpsArr[1]);
    } else {
      this.plotCreateEditForm.controls['latitude_longitude'].setErrors({
        invalid: true,
      });
      this.plotCreateEditForm.controls['latitude'].setValue(null);
      this.plotCreateEditForm.controls['longitude'].setValue(null);
    }
  }

  public onCloseSearchPopover(): void {
    this.plotCreateEditForm.removeControl('districts');
    // Применяем валидатор только если участок включен
    const validator = this.isVisibleSector ? minLengthArray(1) : null;
    this.plotCreateEditForm.addControl('districts', this.fb.array([], validator));
    this.districtGroupList = {};

    each(this.districtList, (d) => {
      if (d.is_checked) {
        (this.plotCreateEditForm.controls['districts'] as UntypedFormArray).push(new UntypedFormControl(d));
        this.districtAddGroup(d);
      }
    });
  }

  public districtAddGroup(district): void {
    const c = cloneDeep(district.city);
    const d = cloneDeep(district);
    delete d.city;

    if (!this.districtGroupList[c.ct_id]) {
      this.districtGroupList[c.ct_id] = c;
      this.districtGroupList[c.ct_id]['districts'] = [];
      this.districtGroupList[c.ct_id]['districts'].push(d);

      return;
    }

    if (this.districtGroupList[c.ct_id]['districts'].findIndex((dist) => dist.d_id === d.d_id) === -1) {
      this.districtGroupList[c.ct_id]['districts'].push(d);
    }
  }

  public onChangeSectorVisible(isChecked: boolean): void {
    const currentSector = this.currentSelectedPlot;

    // Управление валидацией для districts
    const districtsControl = this.plotCreateEditForm.get('districts');
    if (isChecked) {
      // Если участок включен - районы обязательны
      districtsControl.setValidators([minLengthArray(1)]);
    } else {
      // Если участок отключен - районы не обязательны
      districtsControl.clearValidators();
    }
    districtsControl.updateValueAndValidity();

    if (!isChecked && this.isEditMode && currentSector.is_visible) {
      this.plotCreateEditForm.setControl('migrate_users', new UntypedFormArray([]));
      this.getMigrateSectors();
    } else {
      this.plotCreateEditForm.setControl('migrate_users', new UntypedFormControl(null));
    }

    this.checkVisibleMigrateBlock();
  }

  public onChangeSelectedSectorForMigrateEmployers(sector: SectorNames): void {
    this.sectorSelectedForMigrateUsers = sector;
  }

  public onChangeSectorCheckedEmployers(): void {
    const sector = this.sectorSelectedForMigrateUsers;
    const users = this.employersSectorsList.filter((employee) => employee['is_checked']);

    this.isAllEmployeeChecked = false;
    this.isSomeMigrateEmployeeChecked = false;
    each(users, (user) => {
      this.onChangeSectorForMigrateEmployee(sector, user);
      user['is_checked'] = false;
    });
  }

  public onCheckAllEmployers(event): void {
    this.isAllEmployeeChecked = event.target.checked;
    this.isSomeMigrateEmployeeChecked = event.target.checked;
    each(this.employersSectorsList, (employee) => {
      employee['is_checked'] = this.isAllEmployeeChecked;
    });
  }

  public onChangeSectorForMigrateEmployee(sector: SectorNames, user: MigrateEmployer): void {
    const migrateUsers = this.getMigrateUsers();

    user.sector = sector || null;
    user.sector_id = (sector && sector.sector_id) || null;

    migrateUsers.controls.forEach((control) => {
      if (control.value.u_id === user.u_id) {
        control.setValue(user);
      }
    });
  }

  public onCheckEmployee(event, employee: MigrateEmployer): void {
    employee['is_checked'] = event.target.checked;

    this.checkCheckedAllEmployers();
  }

  public checkCheckedAllEmployers(): void {
    const checkedItems = this.employersSectorsList.filter((user) => user['is_checked']);
    this.isSomeMigrateEmployeeChecked = checkedItems.length > 0;
    this.isAllEmployeeChecked = checkedItems.length === this.employersSectorsList.length;
  }

  public checkVisibleMigrateBlock(): void {
    const curSelectedSector = this.currentSelectedPlot;
    const sectorForm = this.plotCreateEditForm;
    this.isVisibleMigrateEmployersBlock =
      !!(this.isEditMode && curSelectedSector.is_visible) &&
      ((this.companySelected && this.companySelected.campaign_id !== curSelectedSector.partnership_id) || !!(!sectorForm.value.is_visible && curSelectedSector.is_visible));
  }

  public onChangePlotVersion(version: string): void {
    this.plotVersions.forEach((ver) => {
      if (ver.version_number === version) {
        this.currentSelectedPlot = cloneDeep(ver);
        this.dataLoading = true;
        this.makeEditPlotForm();

        setTimeout(() => {
          this.dataLoading = false;
        }, 500);
      }
    });
  }

  public onSaveStepsCallback(): void {
    if (this.saveOrCreateLoading) {
      return;
    }
    const fieldsInvalid = [];

    this.fieldsInvalid = null;

    each(this.plotCreateEditForm.controls, (value, key) => {
      if (this.plotCreateEditForm.controls[key].invalid) {
        fieldsInvalid.push(key);
      }
    });

    if (this.showDistrictCoefficients && this.districtCoefficients?.invalid) {
      fieldsInvalid.push('district_coefficients');
    }

    this.fieldsInvalid = fieldsInvalid;
    this.backendError = null;

    if (!fieldsInvalid.length) {
      if (this.isEditMode) {
        const confirmCloseStepsComponent = this.modalService.open(ConfirmModalComponent, {
          ariaLabelledBy: 'modal-basic-title',
          windowClass: 'modal-wrapper',
          backdrop: 'static',
        });

        confirmCloseStepsComponent.componentInstance.title = 'Подтвердите сохранение';
        confirmCloseStepsComponent.componentInstance.description = 'Вы внесли изменения в данные участка.' + ' Вы действительно хотите сохранить изменения?';
        confirmCloseStepsComponent.componentInstance.successButtonName = 'Да, сохранить';
        confirmCloseStepsComponent.componentInstance.abortButtonName = 'Отмена';

        confirmCloseStepsComponent.result.then(
          () => {
            this.saveOrEditPlot();
          },
          () => {}
        );
      } else {
        this.saveOrEditPlot();
      }
    }
  }

  public onResetStepsCallback(): void {
    const confirmCloseStepsComponent = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    confirmCloseStepsComponent.componentInstance.title = 'Подтвердите выход без сохранения';
    confirmCloseStepsComponent.componentInstance.description = 'Вся введенная вами информация будет утеряна.' + ' Вы действительно хотите покинуть страницу без сохранения?';
    confirmCloseStepsComponent.componentInstance.successButtonName = 'Да, выйти';
    confirmCloseStepsComponent.componentInstance.abortButtonName = 'Отмена';

    confirmCloseStepsComponent.result.then(
      () => {
        this.router.navigate([SECTORS_PATHS.sectors]);
      },
      () => {}
    );
  }

  public saveOrEditPlot(): void {
    if (this.saveOrCreateLoading || (this.showDistrictCoefficients && this.districtCoefficients?.invalid)) {
      return;
    }
    let requestObj = cloneDeep(this.plotCreateEditForm.value);
    delete requestObj.latitude_longitude;

    /** При использовании reactive forms заблоченных полей нет в списке this.companyCreateEditForm.value :( **/
    if (!this.isEditMode) {
      requestObj['is_visible'] = 1;
    } else {
      requestObj['is_visible'] = +this.plotCreateEditForm.value['is_visible'];
    }

    requestObj['districts'] = requestObj.districts.map((d) => {
      delete d.new_district_name;
      return d;
    });

    if (requestObj['migrate_users']) {
      requestObj['migrate_users'] = requestObj['migrate_users'].map((u) => {
        return { sector_id: u.sector_id, u_id: u.u_id };
      });
    }

    this.saveOrCreateLoading = true;

    const coefficientsEditor = this.showDistrictCoefficients ? this.districtCoefficients : null;
    const saveCoefficients = coefficientsEditor ? coefficientsEditor.save() : of(null);
    saveCoefficients.pipe(
      switchMap(() => this.sectorsService.createOrUpdateSector(requestObj)),
      finalize(() => {
        this.saveOrCreateLoading = false;
        coefficientsEditor?.finishSaving();
      }),
    ).subscribe(
      (response) => {
        this.router.navigate([SECTORS_PATHS.sectors]);
      },
      (error) => {
        console.log(error);
        if (error instanceof Error && !has(error, 'error')) {
          this.backendError = error.message;
        } else if (has(error, 'error.data.sector_head')) {
          this.backendError = error.error.data.sector_head;
        } else if (has(error, 'error.data.non_field_errors')) {
          this.backendError = error.error.data['non_field_errors'][0];
        } else if (has(error, 'error.metadata.message')) {
          this.backendError = error.error.metadata.message;
        } else {
          this.backendError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
        }
      }
    );
  }
}
